import { useCallback, useEffect, useRef, useState } from "react";
import { usePoll } from "./usePoll";
import { useToast } from "../store/ToastProvider";
import {
  AIJobProgress,
  AIJobStatusResponse,
} from "../types/compassAITypes";
import {
  JobStateV2,
  isTerminal,
} from "../types/engagementAssessmentV2Types";
import { MESSAGES } from "../messages";

export const POLL_INTERVAL_MS = 2000;

export const MAX_POLL_ATTEMPTS = 300;

interface UseAssessmentJobPollerReturn<TResult> {
  state: JobStateV2<TResult>;
  startPolling: (jobId: string) => void;
  stopPolling: () => void;
}

export function useAssessmentJobPoller<
  TResult = Record<string, unknown>,
>(): UseAssessmentJobPollerReturn<TResult> {
  const { addToast } = useToast();

  const [state, setState] = useState<JobStateV2<TResult>>({ kind: "idle" });

  const jobIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const toastedRef = useRef<boolean>(false);

  const {
    data: completedJob,
    latestResponse,
    error: pollError,
    startPolling: startUsePoll,
    stopPolling: stopUsePoll,
  } = usePoll<AIJobStatusResponse<TResult>, { job_id: string }>(
    (response) => response.status === "COMPLETED",
    (response) => response.status === "FAILED",
    POLL_INTERVAL_MS,
    MAX_POLL_ATTEMPTS,
  );

  useEffect(() => {
    if (!latestResponse) return;
    const jobId = jobIdRef.current;
    if (!jobId) return;

    if (
      latestResponse.status === "COMPLETED" ||
      latestResponse.status === "FAILED"
    ) {
      return;
    }

    setState((prev) => {
      if (isTerminal(prev)) return prev;

      if (latestResponse.status === "PENDING") {
        return { kind: "queued", jobId };
      }

      const totalSteps = latestResponse.total_steps ?? 1;
      const currentStep = latestResponse.current_step ?? 0;
      const progress: AIJobProgress = {
        currentStep,
        totalSteps,
        stepLabel: latestResponse.step_label ?? "",
        stepDetail: latestResponse.step_detail,
        percentage:
          totalSteps > 0 ? Math.round((currentStep / totalSteps) * 100) : 0,
      };
      return { kind: "running", jobId, progress };
    });
  }, [latestResponse]);

  useEffect(() => {
    if (!completedJob) return;
    const jobId = jobIdRef.current;
    if (!jobId) return;

    const payload = completedJob.result_payload;
    const isValidPayload =
      payload !== null &&
      typeof payload === "object" &&
      !Array.isArray(payload);

    setState((prev) => {
      if (isTerminal(prev)) return prev;
      if (isValidPayload) {
        return { kind: "success", jobId, result: payload as TResult };
      }
      const message = "Assessment completed with an invalid payload.";
      if (!toastedRef.current) {
        toastedRef.current = true;
        addToast(
          MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.POLL_FAILED,
          "error",
        );
      }
      return {
        kind: "failed",
        jobId,
        error: new Error(message),
        message,
      };
    });
  }, [completedJob, addToast]);

  useEffect(() => {
    if (!pollError) return;
    const jobId = jobIdRef.current;
    if (!jobId) return;

    const isTimeoutError =
      pollError.error_string === "Max polling attempts reached";

    setState((prev) => {
      if (isTerminal(prev)) return prev;

      if (isTimeoutError) {
        const elapsedMs =
          startTimeRef.current !== null
            ? Date.now() - startTimeRef.current
            : MAX_POLL_ATTEMPTS * POLL_INTERVAL_MS;
        if (!toastedRef.current) {
          toastedRef.current = true;
          addToast(
            MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.POLL_TIMEOUT,
            "error",
          );
        }
        return { kind: "timeout", jobId, elapsedMs };
      }

      const message =
        latestResponse?.error_message ??
        pollError.error_user_message ??
        pollError.error_string ??
        "The assessment job failed.";
      if (!toastedRef.current) {
        toastedRef.current = true;
        addToast(
          MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.POLL_FAILED,
          "error",
        );
      }
      return { kind: "failed", jobId, error: pollError, message };
    });
  }, [pollError, latestResponse, addToast]);

  const startPolling = useCallback(
    (jobId: string): void => {
      jobIdRef.current = jobId;
      startTimeRef.current = Date.now();
      toastedRef.current = false;
      setState({ kind: "queued", jobId });
      startUsePoll("AI_GET_JOB_STATUS", { job_id: jobId });
    },
    [startUsePoll],
  );

  const stopPolling = useCallback((): void => {
    const jobId = jobIdRef.current;
    stopUsePoll();
    setState((prev) => {
      if (isTerminal(prev)) return prev;
      return jobId
        ? { kind: "cancelled_locally", jobId }
        : { kind: "idle" };
    });
  }, [stopUsePoll]);

  useEffect(() => {
    return () => {
      stopUsePoll();
      const jobId = jobIdRef.current;
      if (jobId) {
        setState((prev) => {
          if (isTerminal(prev)) return prev;
          return { kind: "cancelled_locally", jobId };
        });
      }
    };
  }, [stopUsePoll]);

  return { state, startPolling, stopPolling };
}
